import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Plus, Trash2, Users as UsersIcon, Shield, Lock, Eye, EyeOff, Shuffle, KeyRound, Pencil, UserX, UserCheck, ArrowUpDown, ChevronUp, ChevronDown, Copy, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { RolesManager } from '../components/RolesManager';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusConfig = {
  'lead': { label: 'Lead/do pomiaru', color: '#0000FF' },
  'akceptacja': { label: 'Akceptacja', color: '#FFFF00' },
  'brak_akceptacji': { label: 'Brak akceptacji', color: '#696969' },
  'oplacone': { label: 'Opłacone', color: '#2E8B57' },
  'dostawa': { label: 'Dostawa', color: '#800080' },
  'w_realizacji': { label: 'W realizacji', color: '#FFA500' },
  'zakonczone': { label: 'Zakończono', color: '#006400' }
};
const statusOrder = ['lead', 'akceptacja', 'brak_akceptacji', 'oplacone', 'dostawa', 'w_realizacji', 'zakonczone'];

const ROLE_BADGES = {
  'admin': { label: 'Administrator', bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  'user': { label: 'Użytkownik', bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  'handlowiec_b2b': { label: 'Handlowiec B2B', bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  'montaz': { label: 'Montaż', bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  'asystent_biura': { label: 'Asystent biura', bg: 'bg-slate-400/20', text: 'text-[var(--crm-text-secondary)]', border: 'border-slate-400/30' },
  'guest': { label: 'Gość', bg: 'bg-slate-500/10', text: 'text-[var(--crm-text-muted)]', border: 'border-slate-500/20' },
  'duch': { label: 'Duch (Demo)', bg: 'bg-slate-500/10', text: 'text-[var(--crm-text-muted)]', border: 'border-slate-500/20' },
  'lead_feeder': { label: 'Lead Feeder', bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
};

const getRoleBadge = (roleId) => ROLE_BADGES[roleId] || { label: roleId, bg: 'bg-slate-500/20', text: 'text-[var(--crm-text-secondary)]', border: 'border-slate-500/30' };

const generatePassword = () => {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%&*';
  let pw = [upper[Math.floor(Math.random()*upper.length)], digits[Math.floor(Math.random()*digits.length)], special[Math.floor(Math.random()*special.length)]];
  const all = upper + lower + digits + special;
  for (let i = pw.length; i < 10; i++) pw.push(all[Math.floor(Math.random()*all.length)]);
  return pw.sort(() => Math.random() - 0.5).join('');
};

const validatePassword = (pw) => {
  if (pw.length < 8) return 'Hasło musi mieć minimum 8 znaków';
  if (!/[A-Z]/.test(pw)) return 'Hasło musi zawierać przynajmniej 1 dużą literę';
  if (!/[0-9]/.test(pw)) return 'Hasło musi zawierać przynajmniej 1 cyfrę';
  return null;
};

export const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [formData, setFormData] = useState({
    email: '', password: '', initials: '', role: 'user',
    commission_percent: '', commission_base: '', monthly_plan: '', quarterly_plan: '', field_lead_access: false
  });
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortCol, setSortCol] = useState('email');
  const [sortDir, setSortDir] = useState('asc');

  // Edit user dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Password reset dialog
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pwForm, setPwForm] = useState({ password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);

  // Status permissions
  const [userPermissionsList, setUserPermissionsList] = useState([]);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);

  // Lead Feeder dialog
  const [feederDialogOpen, setFeederDialogOpen] = useState(false);
  const [feederForm, setFeederForm] = useState({ name: '', email: '', password: '' });
  const [feederShowPw, setFeederShowPw] = useState(false);
  const [feederCreated, setFeederCreated] = useState(null); // { email, password } after success
  const [feederLoading, setFeederLoading] = useState(false);

  // Feature permissions map (from backend)
  const [featureMap, setFeatureMap] = useState({});
  const [grantableFeatures, setGrantableFeatures] = useState([]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes, permsRes, featRes] = await Promise.all([
        axios.get(`${API_URL}/users`),
        axios.get(`${API_URL}/roles`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/user-status-permissions-list`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/permissions/features`).catch(() => ({ data: { features: {}, grantable: [] } }))
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setUserPermissionsList(permsRes.data || []);
      if (featRes.data?.features) setFeatureMap(featRes.data.features);
      if (featRes.data?.grantable) setGrantableFeatures(featRes.data.grantable);
    } catch { toast.error('Błąd pobierania danych'); } finally { setLoading(false); }
  };

  // Sorted & filtered users
  const sortedUsers = useMemo(() => {
    let list = [...users];
    if (roleFilter !== 'all') list = list.filter(u => u.role === roleFilter);
    list.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortCol === 'email') return a.email.localeCompare(b.email) * dir;
      if (sortCol === 'role') return (a.role || '').localeCompare(b.role || '') * dir;
      if (sortCol === 'created_at') return ((a.created_at || '') > (b.created_at || '') ? 1 : -1) * dir;
      if (sortCol === 'last_login') return ((a.last_login || '') > (b.last_login || '') ? 1 : -1) * dir;
      if (sortCol === 'is_active') return ((a.is_active === false ? 0 : 1) - (b.is_active === false ? 0 : 1)) * dir;
      return 0;
    });
    // Inactive at bottom
    list.sort((a, b) => (a.is_active === false ? 1 : 0) - (b.is_active === false ? 1 : 0));
    return list;
  }, [users, roleFilter, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortHeader = ({ col, children }) => (
    <TableHead className="text-[var(--crm-text-secondary)] font-bold cursor-pointer select-none hover:text-[var(--crm-text-primary)]" onClick={() => toggleSort(col)}>
      <span className="flex items-center gap-1">{children}
        {sortCol === col ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </TableHead>
  );

  // Create user
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Register the user first
      const registerPayload = { email: formData.email, password: formData.password, initials: formData.initials, role: formData.role };
      const res = await axios.post(`${API_URL}/auth/register`, registerPayload);
      const newUserId = res.data.id;

      // If sales fields set, update user with PUT
      const hasCommission = formData.commission_percent || formData.commission_base || formData.monthly_plan || formData.quarterly_plan || formData.field_lead_access;
      if (hasCommission && newUserId) {
        const updatePayload = {};
        if (formData.commission_percent) updatePayload.commission_percent = parseFloat(formData.commission_percent);
        if (formData.commission_base) updatePayload.commission_base = formData.commission_base;
        if (formData.monthly_plan) updatePayload.monthly_plan = parseFloat(formData.monthly_plan);
        if (formData.quarterly_plan) updatePayload.quarterly_plan = parseFloat(formData.quarterly_plan);
        updatePayload.field_lead_access = formData.field_lead_access;
        await axios.put(`${API_URL}/users/${newUserId}`, updatePayload).catch(() => {});
      }

      toast.success('Użytkownik utworzony');
      setDialogOpen(false);
      setFormData({ email: '', password: '', initials: '', role: 'user', commission_percent: '', commission_base: '', monthly_plan: '', quarterly_plan: '', field_lead_access: false });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Błąd tworzenia'); }
  };

  // Delete user
  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API_URL}/users/${userId}`);
      toast.success('Użytkownik usunięty');
      fetchData();
    } catch { toast.error('Błąd usuwania'); }
  };

  // Edit user
  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      email: u.email, initials: u.initials, role: u.role,
      is_active: u.is_active !== false,
      field_lead_access: u.field_lead_access || false,
      commission_percent: u.commission_percent || '',
      commission_base: u.commission_base || '',
      monthly_plan: u.monthly_plan || '',
      quarterly_plan: u.quarterly_plan || '',
      extra_permissions: u.extra_permissions || [],
      removed_permissions: u.removed_permissions || [],
    });
    setEditDialogOpen(true);
  };
  const saveEdit = async () => {
    try {
      const payload = { ...editForm };
      if (payload.commission_percent === '') payload.commission_percent = null;
      else payload.commission_percent = parseFloat(payload.commission_percent);
      if (payload.monthly_plan === '') payload.monthly_plan = null;
      else payload.monthly_plan = parseFloat(payload.monthly_plan);
      if (payload.quarterly_plan === '') payload.quarterly_plan = null;
      else payload.quarterly_plan = parseFloat(payload.quarterly_plan);
      if (payload.commission_base === '') payload.commission_base = null;
      // extra_permissions is already an array

      await axios.put(`${API_URL}/users/${editUser.id}`, payload);
      toast.success('Użytkownik zaktualizowany');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Błąd aktualizacji'); }
  };

  // Toggle status
  const toggleStatus = async (userId) => {
    try {
      const res = await axios.patch(`${API_URL}/users/${userId}/status`);
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error('Błąd zmiany statusu'); }
  };

  // Reset password
  const openPwReset = (u) => {
    setPwUser(u);
    setPwForm({ password: '', confirm: '' });
    setShowPw(false);
    setPwDialogOpen(true);
  };
  const handleGenerate = () => {
    const pw = generatePassword();
    setPwForm({ password: pw, confirm: pw });
    setShowPw(true);
  };
  const savePwReset = async () => {
    const err = validatePassword(pwForm.password);
    if (err) { toast.error(err); return; }
    if (pwForm.password !== pwForm.confirm) { toast.error('Hasła nie są takie same'); return; }
    try {
      await axios.put(`${API_URL}/users/${pwUser.id}/password`, { new_password: pwForm.password });
      toast.success('Hasło zmienione. Przekaż nowe hasło użytkownikowi.');
      setPwDialogOpen(false);
    } catch (error) { toast.error(error.response?.data?.detail || 'Błąd zmiany hasła'); }
  };

  // Permissions helpers
  const getAvailableUsers = () => {
    const withPerms = new Set(userPermissionsList.map(p => p.user_id));
    return users.filter(u => !withPerms.has(u.id) && u.role !== 'admin');
  };
  const handleAddUserPermission = async () => {
    if (!selectedUserId) { toast.error('Wybierz użytkownika'); return; }
    setSavingPermissions(true);
    try {
      await axios.post(`${API_URL}/user-status-permissions`, { user_id: selectedUserId, allowed_statuses: selectedStatuses });
      toast.success('Uprawnienia dodane');
      setAddUserDialogOpen(false); setSelectedUserId(''); setSelectedStatuses([]); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Błąd'); } finally { setSavingPermissions(false); }
  };

  // Lead Feeder creation
  const openFeederDialog = () => {
    setFeederForm({ name: '', email: '', password: '' });
    setFeederCreated(null);
    setFeederShowPw(false);
    setFeederDialogOpen(true);
  };
  const generateFeederPw = () => {
    const pw = generatePassword();
    setFeederForm(prev => ({ ...prev, password: pw }));
    setFeederShowPw(true);
  };
  const handleCreateFeeder = async (e) => {
    e.preventDefault();
    const err = validatePassword(feederForm.password);
    if (err) { toast.error(err); return; }
    if (!feederForm.name.trim()) { toast.error('Podaj imię'); return; }
    if (!feederForm.email.trim()) { toast.error('Podaj email'); return; }
    setFeederLoading(true);
    try {
      const initials = feederForm.name.trim().split(/\s+/).map(w => w[0]?.toUpperCase()).join('').slice(0, 4) || 'LF';
      await axios.post(`${API_URL}/auth/register`, {
        email: feederForm.email,
        password: feederForm.password,
        initials,
        role: 'lead_feeder'
      });
      setFeederCreated({ email: feederForm.email, password: feederForm.password, name: feederForm.name });
      toast.success('Lead Feeder utworzony!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd tworzenia konta');
    } finally { setFeederLoading(false); }
  };
  const copyFeederCredentials = () => {
    if (!feederCreated) return;
    const text = `Trendhomes CRM — Lead Feeder\nImię: ${feederCreated.name}\nLogin: ${feederCreated.email}\nHasło: ${feederCreated.password}\nLink: ${window.location.origin}/login`;
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Dane skopiowane do schowka!');
    }).catch(() => {
      toast.error('Nie udało się skopiować');
    });
  };
  const handleUpdateUserPermission = async (userId, statuses) => {
    try { await axios.put(`${API_URL}/user-status-permissions/${userId}`, { allowed_statuses: statuses }); toast.success('Zaktualizowano'); }
    catch { toast.error('Błąd'); }
  };
  const handleDeleteUserPermission = async (userId) => {
    try { await axios.delete(`${API_URL}/user-status-permissions/${userId}`); toast.success('Usunięto'); fetchData(); }
    catch { toast.error('Błąd'); }
  };
  const handlePermissionChange = (userId, status, checked) => {
    setUserPermissionsList(prev => prev.map(perm => {
      if (perm.user_id === userId) {
        const ns = checked ? [...(perm.allowed_statuses||[]), status] : (perm.allowed_statuses||[]).filter(s => s !== status);
        handleUpdateUserPermission(userId, ns);
        return { ...perm, allowed_statuses: ns };
      }
      return perm;
    }));
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-full"><div className="text-[var(--crm-text-primary)]">Ładowanie...</div></div></Layout>;

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12">
        <div className="mb-6">
          <h1 className="text-2xl md:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight">Użytkownicy</h1>
          <p className="text-[var(--crm-text-secondary)] mt-2">Zarządzaj użytkownikami, rolami i uprawnieniami</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[var(--crm-bg-dialog)] border border-[var(--crm-border)] mb-6">
            <TabsTrigger value="users" className="data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-[var(--crm-text-secondary)]">
              <UsersIcon className="w-4 h-4 mr-2" />Użytkownicy
            </TabsTrigger>
            <TabsTrigger value="roles" className="data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-[var(--crm-text-secondary)]">
              <Shield className="w-4 h-4 mr-2" />Role
            </TabsTrigger>
            <TabsTrigger value="status-permissions" className="data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-[var(--crm-text-secondary)]">
              <Lock className="w-4 h-4 mr-2" />Uprawnienia Statusów
            </TabsTrigger>
          </TabsList>

          {/* ======== USERS TAB ======== */}
          <TabsContent value="users">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <p className="text-[var(--crm-text-secondary)] text-sm">{sortedUsers.length} z {users.length} użytkowników</p>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger data-testid="role-filter" className="w-[180px] h-9 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] text-sm">
                    <SelectValue placeholder="Filtruj po roli" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                    <SelectItem value="all">Wszystkie role</SelectItem>
                    {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={openFeederDialog} data-testid="add-feeder-button"
                  className="bg-orange-500/90 hover:bg-orange-500 text-white">
                  <UserPlus className="w-4 h-4 mr-2" />Dodaj Lead Feedera
                </Button>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-user-button" className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">
                      <Plus className="w-4 h-4 mr-2" />Dodaj Użytkownika
                    </Button>
                  </DialogTrigger>
                <DialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)]">
                  <DialogHeader><DialogTitle className="text-[var(--crm-text-primary)] font-manrope">Nowy Użytkownik</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                    <div>
                      <Label className="text-[var(--crm-text-secondary)]">Email *</Label>
                      <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
                        data-testid="user-email-input" required className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                    </div>
                    <div>
                      <Label className="text-[var(--crm-text-secondary)]">Hasło *</Label>
                      <Input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})}
                        data-testid="user-password-input" required className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                    </div>
                    <div>
                      <Label className="text-[var(--crm-text-secondary)]">Inicjały *</Label>
                      <Input value={formData.initials} onChange={e => setFormData({...formData, initials: e.target.value.toUpperCase()})}
                        data-testid="user-initials-input" maxLength={5} required className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                    </div>
                    <div>
                      <Label className="text-[var(--crm-text-secondary)]">Rola *</Label>
                      <Select value={formData.role} onValueChange={val => setFormData({...formData, role: val, field_lead_access: val === 'handlowiec_b2b'})}>
                        <SelectTrigger data-testid="user-role-select" className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                          {roles.filter(r => r.id !== 'montaz').map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Commission section - shown for handlowiec_b2b or when user wants */}
                    {(formData.role === 'handlowiec_b2b' || formData.role === 'user' || formData.role === 'asystent_biura') && (
                      <div className="border-t border-[var(--crm-border)] pt-3">
                        <p className="text-[var(--crm-text-secondary)] text-xs mb-3 uppercase tracking-wider font-semibold">Prowizja i plan sprzedaży</p>
                        <div className="flex items-center gap-2 mb-3">
                          <Checkbox checked={formData.field_lead_access} onCheckedChange={v => setFormData({...formData, field_lead_access: v})}
                            data-testid="create-user-field-access"
                            className="border-slate-500 data-[state=checked]:bg-[#FF6B35] data-[state=checked]:border-[#FF6B35]" />
                          <Label className="text-[var(--crm-text-secondary)] text-sm">Może tworzyć leady z terenu</Label>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-[var(--crm-text-secondary)] text-xs">Prowizja %</Label>
                            <Input type="number" step="0.1" min="0" max="100" value={formData.commission_percent}
                              onChange={e => setFormData({...formData, commission_percent: e.target.value})}
                              data-testid="create-user-commission" placeholder="np. 5.0"
                              className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                          </div>
                          <div>
                            <Label className="text-[var(--crm-text-secondary)] text-xs">Baza prowizji</Label>
                            <Select value={formData.commission_base} onValueChange={v => setFormData({...formData, commission_base: v})}>
                              <SelectTrigger className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"><SelectValue placeholder="Wybierz..." /></SelectTrigger>
                              <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                                <SelectItem value="total_offer">Od wartości oferty</SelectItem>
                                <SelectItem value="net_margin">Od marży netto</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[var(--crm-text-secondary)] text-xs">Plan miesięczny (zł)</Label>
                            <Input type="number" min="0" value={formData.monthly_plan}
                              onChange={e => setFormData({...formData, monthly_plan: e.target.value})}
                              data-testid="create-user-monthly" placeholder="np. 50000"
                              className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                          </div>
                          <div>
                            <Label className="text-[var(--crm-text-secondary)] text-xs">Plan kwartalny (zł)</Label>
                            <Input type="number" min="0" value={formData.quarterly_plan}
                              onChange={e => setFormData({...formData, quarterly_plan: e.target.value})}
                              data-testid="create-user-quarterly" placeholder="np. 150000"
                              className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                          </div>
                        </div>
                      </div>
                    )}
                    <Button type="submit" data-testid="submit-user-button" className="w-full bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">
                      Utwórz Użytkownika
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[var(--crm-bg-panel)] border-b border-[var(--crm-border)] hover:bg-[var(--crm-bg-panel)]">
                    <SortHeader col="email">Email</SortHeader>
                    <TableHead className="text-[var(--crm-text-secondary)] font-bold">Inicjały</TableHead>
                    <SortHeader col="role">Rola</SortHeader>
                    <SortHeader col="is_active">Status</SortHeader>
                    <SortHeader col="created_at">Data utworzenia</SortHeader>
                    <SortHeader col="last_login">Ostatnie logowanie</SortHeader>
                    <TableHead className="text-[var(--crm-text-secondary)] font-bold">Akcje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedUsers.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-[var(--crm-text-secondary)] py-8">Brak użytkowników</TableCell></TableRow>
                  ) : sortedUsers.map(user => {
                    const rb = getRoleBadge(user.role);
                    const inactive = user.is_active === false;
                    return (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}
                        className={`border-b border-[var(--crm-border)] transition-colors ${inactive ? 'bg-slate-800/30 opacity-60' : 'hover:bg-[var(--crm-bg-card-hover)]'}`}>
                        <TableCell className="text-[var(--crm-text-primary)] font-medium">{user.email}</TableCell>
                        <TableCell><Badge className="bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-primary)]">{user.initials}</Badge></TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${rb.bg} ${rb.text} ${rb.border}`}>
                            {rb.label}
                          </span>
                          {(user.role === 'handlowiec_b2b' || user.commission_percent) && user.commission_percent > 0 && (
                            <div className="text-[10px] text-[var(--crm-text-muted)] mt-0.5">
                              {user.commission_percent}% · {user.monthly_plan ? `${(user.monthly_plan/1000).toFixed(0)}k/mies.` : '—'}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {inactive
                            ? <Badge className="bg-red-500/15 text-red-400 border border-red-500/30">Nieaktywny</Badge>
                            : <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Aktywny</Badge>}
                        </TableCell>
                        <TableCell className="text-[var(--crm-text-secondary)] text-sm">{new Date(user.created_at).toLocaleDateString('pl-PL')}</TableCell>
                        <TableCell className="text-[var(--crm-text-secondary)] text-sm">
                          {user.last_login ? new Date(user.last_login).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button size="sm" onClick={() => openEdit(user)} data-testid={`edit-user-${user.id}`}
                              className="bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-btn-secondary-hover)] text-[var(--crm-text-primary)] h-8 px-2" title="Edytuj">
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" onClick={() => openPwReset(user)} data-testid={`reset-pw-${user.id}`}
                              className="bg-amber-600/80 hover:bg-amber-600 text-[var(--crm-text-primary)] h-8 px-2" title="Resetuj hasło">
                              <KeyRound className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" onClick={() => toggleStatus(user.id)} data-testid={`toggle-status-${user.id}`}
                              className={`h-8 px-2 ${inactive ? 'bg-emerald-600/80 hover:bg-emerald-600' : 'bg-slate-600 hover:bg-slate-500'} text-white`}
                              title={inactive ? 'Aktywuj' : 'Dezaktywuj'}>
                              {inactive ? <UserCheck className="w-3.5 h-3.5" /> : <UserX className="w-3.5 h-3.5" />}
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" data-testid={`delete-user-${user.id}`} className="bg-red-600/80 hover:bg-red-600 text-white h-8 px-2" title="Usuń">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)]">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-[var(--crm-text-primary)]">Potwierdź usunięcie</AlertDialogTitle>
                                  <AlertDialogDescription className="text-[var(--crm-text-secondary)]">Czy na pewno chcesz usunąć użytkownika {user.email}?</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-transparent border-[var(--crm-border)] text-[var(--crm-text-secondary)]">Anuluj</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(user.id)} className="bg-red-600 hover:bg-red-700 text-white">Usuń</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* ======== ROLES TAB ======== */}
          <TabsContent value="roles"><RolesManager /></TabsContent>

          {/* ======== STATUS PERMISSIONS TAB ======== */}
          <TabsContent value="status-permissions">
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-[var(--crm-text-secondary)]">Zarządzaj uprawnieniami użytkowników do zmiany statusów ({userPermissionsList.length})</p>
                <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white"><Plus className="w-4 h-4 mr-2" />Dodaj Użytkownika</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] max-w-md">
                    <DialogHeader><DialogTitle className="text-[var(--crm-text-primary)]">Dodaj Uprawnienia</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-[var(--crm-text-secondary)]">Użytkownik *</Label>
                        <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                          <SelectTrigger className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] mt-1"><SelectValue placeholder="Wybierz..." /></SelectTrigger>
                          <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                            {getAvailableUsers().map(u => <SelectItem key={u.id} value={u.id}>{u.initials} - {u.email}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[var(--crm-text-secondary)] mb-2 block">Statusy</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {statusOrder.map(s => (
                            <div key={s} className="flex items-center gap-2 p-2 rounded bg-[var(--crm-bg-panel)] border border-[var(--crm-border)]">
                              <Checkbox checked={selectedStatuses.includes(s)} onCheckedChange={() => setSelectedStatuses(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s])}
                                className="border-slate-500 data-[state=checked]:bg-[#FF6B35] data-[state=checked]:border-[#FF6B35]" />
                              <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: statusConfig[s].color }} />
                              <span className="text-[var(--crm-text-secondary)] text-xs">{statusConfig[s].label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button onClick={handleAddUserPermission} disabled={savingPermissions || !selectedUserId}
                        className="w-full bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">{savingPermissions ? 'Zapisywanie...' : 'Dodaj'}</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <Card className="bg-[var(--crm-bg-card)] border-[var(--crm-border)] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[var(--crm-border)]">
                        <th className="text-left p-4 text-[var(--crm-text-secondary)] font-medium"><div className="flex items-center gap-2"><UsersIcon className="w-4 h-4" />Użytkownik</div></th>
                        {statusOrder.map(s => (
                          <th key={s} className="text-center p-3 min-w-[80px]">
                            <div className="w-4 h-4 rounded-full mx-auto mb-1" style={{ backgroundColor: statusConfig[s].color }} />
                            <span className="text-[var(--crm-text-secondary)] text-xs font-medium">{statusConfig[s].label}</span>
                          </th>
                        ))}
                        <th className="text-center p-3 text-[var(--crm-text-secondary)] font-medium">Akcje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.filter(u => u.role === 'admin').map(admin => (
                        <tr key={admin.id} className="border-b border-[var(--crm-border)] bg-[var(--crm-bg-panel)]/50">
                          <td className="p-4"><div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#FF6B35] flex items-center justify-center text-white text-xs font-bold">{admin.initials}</div>
                            <div><span className="text-[var(--crm-text-primary)] font-medium">{admin.email}</span><div className="flex items-center gap-1"><Badge className="bg-[#FF6B35] text-white text-xs">Admin</Badge><span className="text-xs text-[var(--crm-text-muted)]">(wszystkie)</span></div></div>
                          </div></td>
                          {statusOrder.map(s => <td key={s} className="text-center p-3"><Checkbox checked disabled className="opacity-50" /></td>)}
                          <td className="text-center p-3"><span className="text-[var(--crm-text-muted)] text-xs">-</span></td>
                        </tr>
                      ))}
                      {userPermissionsList.map(perm => {
                        const u = users.find(x => x.id === perm.user_id);
                        if (!u) return null;
                        const rb = getRoleBadge(u.role);
                        return (
                          <tr key={perm.user_id} className="border-b border-[var(--crm-border)] hover:bg-[var(--crm-bg-panel)]/30">
                            <td className="p-4"><div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[var(--crm-bg-btn-secondary)] flex items-center justify-center text-[var(--crm-text-primary)] text-xs font-bold">{u.initials}</div>
                              <div><span className="text-[var(--crm-text-primary)] font-medium">{u.email}</span><div><span className={`text-xs px-1.5 py-0.5 rounded ${rb.bg} ${rb.text}`}>{rb.label}</span></div></div>
                            </div></td>
                            {statusOrder.map(s => (
                              <td key={s} className="text-center p-3">
                                <Checkbox checked={(perm.allowed_statuses||[]).includes(s)} onCheckedChange={c => handlePermissionChange(perm.user_id, s, c)}
                                  className="border-slate-500 data-[state=checked]:bg-[#FF6B35] data-[state=checked]:border-[#FF6B35]" />
                              </td>
                            ))}
                            <td className="text-center p-3">
                              <AlertDialog>
                                <AlertDialogTrigger asChild><Button size="sm" className="bg-red-600 hover:bg-red-700 text-white h-8 px-2"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                                <AlertDialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)]">
                                  <AlertDialogHeader><AlertDialogTitle className="text-[var(--crm-text-primary)]">Potwierdź usunięcie</AlertDialogTitle><AlertDialogDescription className="text-[var(--crm-text-secondary)]">Usunąć uprawnienia dla {u.email}?</AlertDialogDescription></AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="bg-transparent border-[var(--crm-border)] text-[var(--crm-text-secondary)]">Anuluj</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteUserPermission(perm.user_id)} className="bg-red-600 hover:bg-red-700 text-white">Usuń</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ======== EDIT USER DIALOG ======== */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] max-w-lg">
          <DialogHeader><DialogTitle className="text-[var(--crm-text-primary)] font-manrope">Edycja użytkownika: {editUser?.email}</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[var(--crm-text-secondary)]">Email</Label>
                  <Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})}
                    data-testid="edit-user-email" className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                </div>
                <div>
                  <Label className="text-[var(--crm-text-secondary)]">Inicjały</Label>
                  <Input value={editForm.initials} onChange={e => setEditForm({...editForm, initials: e.target.value.toUpperCase()})}
                    data-testid="edit-user-initials" maxLength={5} className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[var(--crm-text-secondary)]">Rola</Label>
                  <Select value={editForm.role} onValueChange={v => setEditForm({...editForm, role: v})}>
                    <SelectTrigger data-testid="edit-user-role" className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                      {roles.filter(r => r.id !== 'montaz' || r.id === editForm.role).map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-3 pb-1">
                  <div className="flex items-center gap-2">
                    <Switch checked={editForm.is_active} onCheckedChange={v => setEditForm({...editForm, is_active: v})}
                      data-testid="edit-user-active" />
                    <Label className="text-[var(--crm-text-secondary)] text-sm">{editForm.is_active ? 'Aktywny' : 'Nieaktywny'}</Label>
                  </div>
                </div>
              </div>
              <div className="border-t border-[var(--crm-border)] pt-4">
                <p className="text-[var(--crm-text-secondary)] text-xs mb-3 uppercase tracking-wider">Ustawienia sprzedaży</p>
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox checked={editForm.field_lead_access} onCheckedChange={v => setEditForm({...editForm, field_lead_access: v})}
                    data-testid="edit-user-field-access"
                    className="border-slate-500 data-[state=checked]:bg-[#FF6B35] data-[state=checked]:border-[#FF6B35]" />
                  <Label className="text-[var(--crm-text-secondary)] text-sm">Dostęp do leada terenowego</Label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[var(--crm-text-secondary)] text-sm">Prowizja %</Label>
                    <Input type="number" step="0.1" value={editForm.commission_percent} onChange={e => setEditForm({...editForm, commission_percent: e.target.value})}
                      data-testid="edit-user-commission" placeholder="np. 5.0" className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                  </div>
                  <div>
                    <Label className="text-[var(--crm-text-secondary)] text-sm">Baza prowizji</Label>
                    <Select value={editForm.commission_base || ''} onValueChange={v => setEditForm({...editForm, commission_base: v})}>
                      <SelectTrigger className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]"><SelectValue placeholder="Wybierz..." /></SelectTrigger>
                      <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]">
                        <SelectItem value="total_offer">Oferta brutto</SelectItem>
                        <SelectItem value="margin">Marża</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[var(--crm-text-secondary)] text-sm">Plan miesięczny (zł)</Label>
                    <Input type="number" value={editForm.monthly_plan} onChange={e => setEditForm({...editForm, monthly_plan: e.target.value})}
                      data-testid="edit-user-monthly" placeholder="np. 50000" className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                  </div>
                  <div>
                    <Label className="text-[var(--crm-text-secondary)] text-sm">Plan kwartalny (zł)</Label>
                    <Input type="number" value={editForm.quarterly_plan} onChange={e => setEditForm({...editForm, quarterly_plan: e.target.value})}
                      data-testid="edit-user-quarterly" placeholder="np. 150000" className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                  </div>
                </div>
              </div>
              {/* ── Dodatkowy dostęp (extra_permissions) ── */}
              {grantableFeatures.length > 0 && (
                <div className="border-t border-[var(--crm-border)] pt-4">
                  <p className="text-[var(--crm-text-secondary)] text-xs mb-3 uppercase tracking-wider font-semibold">Zarządzanie dostępem</p>
                  <p className="text-[var(--crm-text-muted)] text-[10px] mb-3">Zaznacz/odznacz funkcje. Zielone = z roli, pomarańczowe = nadane indywidualnie, szare = odebrane.</p>
                  <div className="grid grid-cols-2 gap-2">
                    {grantableFeatures.map(feat => {
                      const def = featureMap[feat] || {};
                      const roleHasIt = (def.roles || []).includes(editForm.role);
                      const isExtra = (editForm.extra_permissions || []).includes(feat);
                      const isRemoved = (editForm.removed_permissions || []).includes(feat);
                      // Final state: active if (roleHasIt or isExtra) and not isRemoved
                      const isActive = (roleHasIt || isExtra) && !isRemoved;

                      const handleToggle = (checked) => {
                        setEditForm(prev => {
                          const extra = [...(prev.extra_permissions || [])];
                          const removed = [...(prev.removed_permissions || [])];

                          if (checked) {
                            // Turning ON
                            // Remove from removed_permissions
                            const ri = removed.indexOf(feat);
                            if (ri !== -1) removed.splice(ri, 1);
                            // If role doesn't have it, add to extra
                            if (!roleHasIt && !extra.includes(feat)) extra.push(feat);
                          } else {
                            // Turning OFF
                            // Remove from extra_permissions
                            const ei = extra.indexOf(feat);
                            if (ei !== -1) extra.splice(ei, 1);
                            // If role has it, add to removed_permissions
                            if (roleHasIt && !removed.includes(feat)) removed.push(feat);
                          }
                          return { ...prev, extra_permissions: extra, removed_permissions: removed };
                        });
                      };

                      let statusColor = 'text-[var(--crm-text-muted)]';
                      let bgClass = 'bg-[var(--crm-bg-panel)] border-[var(--crm-border)]';
                      let statusLabel = '';
                      if (isActive && roleHasIt && !isExtra) {
                        statusColor = 'text-emerald-400';
                        bgClass = 'bg-emerald-500/10 border-emerald-500/20';
                        statusLabel = '(z roli)';
                      } else if (isActive && isExtra) {
                        statusColor = 'text-[#FF6B35]';
                        bgClass = 'bg-orange-500/10 border-orange-500/20';
                        statusLabel = '(nadane)';
                      } else if (isRemoved && roleHasIt) {
                        statusColor = 'text-red-400';
                        bgClass = 'bg-red-500/5 border-red-500/20';
                        statusLabel = '(odebrane)';
                      }

                      return (
                        <div key={feat} className={`flex items-center gap-2 p-2 rounded border ${bgClass}`}>
                          <Checkbox
                            checked={isActive}
                            onCheckedChange={handleToggle}
                            data-testid={`perm-${feat}`}
                            className="border-slate-500 data-[state=checked]:bg-[#FF6B35] data-[state=checked]:border-[#FF6B35]"
                          />
                          <span className={`text-xs ${statusColor}`}>
                            {def.label || feat}
                            {statusLabel && <span className="text-[10px] opacity-70 ml-1">{statusLabel}</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <Button onClick={saveEdit} data-testid="save-edit-user" className="w-full bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">Zapisz zmiany</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ======== PASSWORD RESET DIALOG ======== */}
      <Dialog open={pwDialogOpen} onOpenChange={setPwDialogOpen}>
        <DialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] max-w-md">
          <DialogHeader><DialogTitle className="text-[var(--crm-text-primary)] font-manrope">Resetuj hasło: {pwUser?.email}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[var(--crm-text-secondary)]">Nowe hasło</Label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} value={pwForm.password}
                  onChange={e => setPwForm({...pwForm, password: e.target.value})}
                  data-testid="new-password-input"
                  className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] pr-10" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[var(--crm-text-secondary)]">Powtórz hasło</Label>
              <Input type={showPw ? 'text' : 'password'} value={pwForm.confirm}
                onChange={e => setPwForm({...pwForm, confirm: e.target.value})}
                data-testid="confirm-password-input"
                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
            </div>
            {pwForm.password && pwForm.password !== pwForm.confirm && (
              <p className="text-red-400 text-xs">Hasła nie są takie same</p>
            )}
            {pwForm.password && validatePassword(pwForm.password) && (
              <p className="text-amber-400 text-xs">{validatePassword(pwForm.password)}</p>
            )}
            <Button type="button" onClick={handleGenerate} data-testid="generate-pw-btn"
              className="w-full bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-btn-secondary-hover)] text-[var(--crm-text-primary)]">
              <Shuffle className="w-4 h-4 mr-2" />Generuj losowe hasło
            </Button>
            <div className="flex gap-3">
              <Button onClick={() => setPwDialogOpen(false)} className="flex-1 bg-transparent border border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)]">Anuluj</Button>
              <Button onClick={savePwReset} data-testid="save-pw-btn"
                disabled={!pwForm.password || pwForm.password !== pwForm.confirm || !!validatePassword(pwForm.password)}
                className="flex-1 bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">Zapisz</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ======== LEAD FEEDER DIALOG ======== */}
      <Dialog open={feederDialogOpen} onOpenChange={(open) => { setFeederDialogOpen(open); if (!open) setFeederCreated(null); }}>
        <DialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[var(--crm-text-primary)] font-manrope flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-orange-400" />
              {feederCreated ? 'Lead Feeder utworzony!' : 'Nowy Lead Feeder'}
            </DialogTitle>
          </DialogHeader>

          {feederCreated ? (
            <div className="space-y-4">
              <div className="bg-[var(--crm-bg-panel)] border border-emerald-500/30 rounded-lg p-4">
                <p className="text-emerald-400 text-sm font-semibold mb-3">Konto gotowe — przekaż dane:</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[var(--crm-text-secondary)]">Imię:</span>
                    <span className="text-[var(--crm-text-primary)] font-medium" data-testid="feeder-created-name">{feederCreated.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--crm-text-secondary)]">Login:</span>
                    <span className="text-[var(--crm-text-primary)] font-medium" data-testid="feeder-created-email">{feederCreated.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--crm-text-secondary)]">Hasło:</span>
                    <span className="text-[var(--crm-text-primary)] font-mono font-medium" data-testid="feeder-created-password">{feederCreated.password}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--crm-text-secondary)]">Link:</span>
                    <span className="text-blue-400 text-xs font-medium">{window.location.origin}/login</span>
                  </div>
                </div>
              </div>
              <Button onClick={copyFeederCredentials} data-testid="copy-feeder-credentials-button"
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-base">
                <Copy className="w-4 h-4 mr-2" />
                Kopiuj dane do wysłania
              </Button>
              <p className="text-[var(--crm-text-muted)] text-xs text-center">Skopiowane dane wyślij np. SMS-em lub komunikatorem</p>
            </div>
          ) : (
            <form onSubmit={handleCreateFeeder} className="space-y-4">
              <div className="bg-[var(--crm-bg-panel)] border border-orange-500/20 rounded-lg p-3 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-400" />
                <p className="text-[var(--crm-text-secondary)] text-xs">Rola <span className="text-orange-400 font-semibold">Lead Feeder</span> — dostęp wyłącznie do formularza leadów</p>
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)]">Imię *</Label>
                <Input value={feederForm.name}
                  onChange={e => setFeederForm(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="feeder-name-input"
                  placeholder="np. Jan Kowalski"
                  required
                  className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] mt-1" />
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)]">Email *</Label>
                <Input type="email" value={feederForm.email}
                  onChange={e => setFeederForm(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="feeder-email-input"
                  placeholder="jan@example.com"
                  required
                  className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] mt-1" />
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)]">Hasło *</Label>
                <div className="flex gap-2 mt-1">
                  <div className="relative flex-1">
                    <Input type={feederShowPw ? 'text' : 'password'} value={feederForm.password}
                      onChange={e => setFeederForm(prev => ({ ...prev, password: e.target.value }))}
                      data-testid="feeder-password-input"
                      placeholder="min. 8 znaków"
                      required
                      className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] pr-10" />
                    <button type="button" onClick={() => setFeederShowPw(!feederShowPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]">
                      {feederShowPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button type="button" onClick={generateFeederPw} data-testid="feeder-generate-pw-button"
                    className="bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-btn-secondary-hover)] text-[var(--crm-text-primary)] shrink-0" title="Generuj hasło">
                    <Shuffle className="w-4 h-4" />
                  </Button>
                </div>
                {feederForm.password && validatePassword(feederForm.password) && (
                  <p className="text-amber-400 text-xs mt-1">{validatePassword(feederForm.password)}</p>
                )}
              </div>
              <Button type="submit" disabled={feederLoading || !feederForm.name || !feederForm.email || !feederForm.password || !!validatePassword(feederForm.password)}
                data-testid="submit-feeder-button"
                className="w-full h-11 bg-orange-500 hover:bg-orange-400 text-white font-semibold">
                {feederLoading ? 'Tworzenie...' : 'Utwórz Lead Feedera'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
